import type { AiProviderName, AppKey } from '@qms360/shared';
import { apiFetch } from './api.js';

/**
 * AI Settings & Controls (spec §13.3).
 *
 * A stored API key never comes back from the server: `storedKey` says whether one
 * is set, its last four characters, and when — nothing more.
 */

export interface AiProviderStatus {
  key: AiProviderName;
  external: boolean;
  demonstrationOnly: boolean;
  takesKey: boolean;
  configured: boolean;
  keySource: 'vault' | 'environment' | null;
  storedKey: { set: boolean; hint: string | null; setAt: string | null; unreadable: boolean } | null;
  defaultModel: string;
  price: { inputPerMillion: number; outputPerMillion: number; currency: string } | null;
}

export interface AiUsageLine {
  month: string;
  feature: string;
  provider: string;
  calls: number;
  timedOut: number;
  failed: number;
  accepted: number;
  edited: number;
  rejected: number;
  inputTokens: number;
  outputTokens: number;
  uncosted: number;
  cost: Record<string, number>;
}

export interface AiSettingsView {
  app: { key: AppKey; label: string };
  provider: AiProviderName | null;
  model: string | null;
  features: { key: string; label: string; enabled: boolean }[];
  guardrails: { sendProjectNames: boolean };
  monthlyQuota: number | null;
  usedThisMonth: number;
  fixed: { humanInTheLoop: boolean; timeoutSeconds: number };
  providers: AiProviderStatus[];
  vaultAvailable: boolean;
  managesKeysAndPrices: boolean;
  usage: AiUsageLine[];
}

export interface AiActivityEntry {
  id: string;
  createdAt: string;
  feature: string;
  fieldName: string | null;
  userName: string | null;
  provider: string;
  model: string;
  promptText: string | null;
  responseText: string | null;
  disposition: string;
  finalText: string | null;
  latencyMs: number | null;
  timedOut: boolean;
  errorMessage: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  estimatedCost: string | null;
}

export const fetchAiSettings = (app: string) => apiFetch<AiSettingsView>(`/admin/ai/${app}`);

export const updateAiSettings = (app: string, body: Record<string, unknown>) =>
  apiFetch<AiSettingsView>(`/admin/ai/${app}`, { method: 'PATCH', body: JSON.stringify(body) });

export const storeAiKey = (provider: string, secret: string) =>
  apiFetch<{ credential: unknown }>(`/admin/ai/credentials/${provider}`, {
    method: 'PUT',
    body: JSON.stringify({ secret }),
  });

export const removeAiKey = (provider: string) =>
  apiFetch<void>(`/admin/ai/credentials/${provider}`, { method: 'DELETE' });

export const setAiPrice = (provider: string, body: { inputPerMillion: number; outputPerMillion: number; currency: string }) =>
  apiFetch<void>(`/admin/ai/prices/${provider}`, { method: 'PUT', body: JSON.stringify(body) });

export const fetchAiActivity = (app: string, before?: string) =>
  apiFetch<{ entries: AiActivityEntry[]; nextCursor: string | null }>(
    `/admin/ai/${app}/activity${before ? `?before=${before}` : ''}`,
  );
